import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil, interval } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { ButtonComponent } from 'wecheer-common';
import {
  CampaignsSafeBoxAdminApiClient,
  CampaignsCampaignAdminApiClient,
  SafeBoxSimulationRowResponse,
  SafeBoxRedeemRowResponse,
  SafeBoxStartSimulationRequest,
  SafeBoxConfirmRedeemRequest,
  SafeBoxConfirmRedeemRow,
  SafeBoxBatchJobSummaryResponse
} from 'src/app/api/api-campaigns.service.generated';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { lastValueFrom } from 'rxjs';

export interface SimulateRedeemLog {
  timestamp: Date;
  level: 'info' | 'success' | 'warning' | 'error';
  message: string;
}

interface StatusMeta {
  label: string;
  icon: string;
  cssClass: string;
}

const STATUS_META: Record<string, StatusMeta> = {
  Redeemable: { label: 'Redeemable', icon: 'check_circle', cssClass: 'status-success' },
  AlreadyRedeemed: { label: 'Already Redeemed', icon: 'block', cssClass: 'status-warning' },
  InvalidCode: { label: 'Invalid Code', icon: 'error', cssClass: 'status-error' },
  CampaignExpired: { label: 'Campaign Expired', icon: 'schedule', cssClass: 'status-error' },
  CodeNotLinkedToCampaign: { label: 'Not Linked To Campaign', icon: 'error', cssClass: 'status-error' },
  CountryNotPermitted: { label: 'Country Not Permitted', icon: 'block', cssClass: 'status-warning' },
  CountryNotPermittedMaxAttempts: { label: 'Country Not Permitted', icon: 'block', cssClass: 'status-warning' },
  UnknownError: { label: 'Error', icon: 'error', cssClass: 'status-error' }
};

const RESULTS_COLUMNS = ['select', 'userId', 'code', 'status', 'reward', 'message'];
const REDEEM_RESULTS_COLUMNS = ['userId', 'code', 'status', 'message'];
const HISTORY_COLUMNS = ['status', 'progress', 'rows', 'startTime', 'duration'];
const PREVIEW_COLUMNS = ['userId', 'code'];

@Component({
  selector: 'app-simulate-redeem-safebox-tab',
  templateUrl: './simulate-redeem-safebox-tab.component.html',
  styleUrls: ['./simulate-redeem-safebox-tab.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatTableModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    NgxDropzoneModule,
    ButtonComponent
  ]
})
export class SimulateRedeemSafeboxTabComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input() advertiserId: string;
  @ViewChild('logContainer', { static: false }) logContainer: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private snackBar = inject(MatSnackBar);
  private safeBoxClient = inject(CampaignsSafeBoxAdminApiClient);
  private campaignAdminClient = inject(CampaignsCampaignAdminApiClient);

  readonly resultsColumns = RESULTS_COLUMNS;
  readonly redeemResultsColumns = REDEEM_RESULTS_COLUMNS;
  readonly historyColumns = HISTORY_COLUMNS;
  readonly previewColumns = PREVIEW_COLUMNS;

  // --- Job history (resume watching after navigating away) ---
  jobHistory: SafeBoxBatchJobSummaryResponse[] = [];
  isLoadingHistory = false;
  selectedHistoryJobId: string | null = null;

  selectedFiles: File[] = [];
  previewRows: SimulateRedeemRowLike[] = [];

  // When off, the campaign's consumption/time limit checks are skipped for every row in this job.
  checkLimitRule = true;

  // --- Simulation phase state ---
  isSimulationRunning = false;
  simulationCompleted = false;
  simulationEverStarted = false;
  simulationId: string | null = null;
  simulationProgress = { total: 0, processed: 0, valid: 0, invalid: 0, percentage: 0 };
  simulationResults: SafeBoxSimulationRowResponse[] = [];
  simulationErrorReportS3Url: string | null = null;
  private simulationMonitorSub: any = null;

  // --- Redeem confirmation / execution phase state ---
  selectedRowKeys = new Set<string>();
  showRedeemConfirmBanner = false;
  isRedeemRunning = false;
  redeemCompleted = false;
  redeemEverStarted = false;
  redeemJobId: string | null = null;
  redeemProgress = { total: 0, processed: 0, success: 0, failed: 0, percentage: 0 };
  redeemResults: SafeBoxRedeemRowResponse[] = [];
  redeemErrorReportS3Url: string | null = null;
  private redeemMonitorSub: any = null;

  logs: SimulateRedeemLog[] = [];
  private shouldScrollToBottom = false;

  ngOnInit(): void {
    this.loadJobHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- Job history (resume watching after navigating away) ----------

  async loadJobHistory(): Promise<void> {
    this.isLoadingHistory = true;
    try {
      this.jobHistory = await lastValueFrom(this.safeBoxClient.getSafeBoxBatchJobHistory(20));
    } catch (error) {
      this.addLog('error', `Failed to load job history: ${error}`);
    } finally {
      this.isLoadingHistory = false;
    }
  }

  /**
   * Shows the just-started job in the Recent Jobs list right away, without waiting on a
   * getSafeBoxBatchJobHistory round-trip - the job row is created asynchronously on the backend,
   * so an immediate refresh could race that write and miss it. The real row (with its actual
   * totalRows/progress) replaces this placeholder next time loadJobHistory runs (e.g. on completion).
   */
  private addJobToHistoryImmediately(jobId: string, jobType: string): void {
    const placeholderJob = new SafeBoxBatchJobSummaryResponse({
      jobId,
      jobType,
      status: 'Running',
      totalRows: 0,
      processedRows: 0,
      successCount: 0,
      failedCount: 0,
      progressPercentage: 0,
      startTime: new Date()
    });
    this.jobHistory = [placeholderJob, ...this.jobHistory.filter(j => j.jobId !== jobId)];
  }

  historyStatusMeta(status: string | undefined): StatusMeta {
    switch (status) {
      case 'Running':
        return { label: 'Running', icon: 'autorenew', cssClass: 'status-warning' };
      case 'Completed':
        return { label: 'Done', icon: 'check_circle', cssClass: 'status-success' };
      case 'Failed':
        return { label: 'Failed', icon: 'error', cssClass: 'status-error' };
      case 'Cancelled':
        return { label: 'Failed', icon: 'block', cssClass: 'status-error' };
      default:
        return { label: status ?? 'Unknown', icon: 'help', cssClass: 'status-warning' };
    }
  }

  /**
   * The Recent Jobs progress bar's fill amount: percentage of total rows that are redeemable
   * (successCount / totalRows), not how much of the job has been processed - e.g. 10 redeemable
   * and 10 invalid out of 20 total fills the bar to 50%, not 100%.
   */
  historyRedeemableRatio(job: SafeBoxBatchJobSummaryResponse): number {
    const total = job.totalRows ?? 0;
    const success = job.successCount ?? 0;
    if (total === 0) {
      return 0;
    }
    return Math.round((success / total) * 100);
  }

  /**
   * Colors the Recent Jobs progress bar by outcome quality, not completion percentage: green when
   * every processed row so far is redeemable, yellow once any row is invalid, red if nothing at all
   * is redeemable. Before any row has been processed yet (fresh/placeholder job) it stays neutral -
   * there's no outcome to judge yet.
   */
  historyProgressColorClass(job: SafeBoxBatchJobSummaryResponse): string {
    const success = job.successCount ?? 0;
    const failed = job.failedCount ?? 0;

    if (success === 0 && failed === 0) {
      return 'progress-neutral';
    }
    if (success === 0) {
      return 'progress-error';
    }
    if (failed === 0) {
      return 'progress-success';
    }
    return 'progress-warning';
  }

  historyDuration(job: SafeBoxBatchJobSummaryResponse): string {
    if (!job.startTime) {
      return '—';
    }
    const start = new Date(job.startTime).getTime();
    const end = job.endTime ? new Date(job.endTime).getTime() : Date.now();
    const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  }

  async selectHistoryJob(job: SafeBoxBatchJobSummaryResponse): Promise<void> {
    if (!job.jobId) {
      return;
    }
    this.selectedHistoryJobId = job.jobId;

    if (job.jobType === 'Simulate') {
      await this.resumeSimulationJob(job);
    } else if (job.jobType === 'Redeem') {
      await this.resumeRedeemJob(job);
    }
  }

  private async resumeSimulationJob(job: SafeBoxBatchJobSummaryResponse): Promise<void> {
    const jobId = job.jobId!;
    this.stopSimulationMonitoring();
    this.resetRedeemState();

    this.simulationId = jobId;
    this.simulationEverStarted = true;
    this.simulationCompleted = false;
    this.simulationResults = [];
    this.simulationErrorReportS3Url = null;
    this.selectedRowKeys.clear();
    this.simulationProgress = {
      total: job.totalRows ?? 0,
      processed: job.processedRows ?? 0,
      valid: job.successCount ?? 0,
      invalid: job.failedCount ?? 0,
      percentage: job.progressPercentage ?? 0
    };

    this.addLog('info', `Resuming simulation ${jobId} (status: ${job.status})`);

    if (job.status === 'Running') {
      this.isSimulationRunning = true;
      this.startSimulationMonitoring(jobId);
    } else if (job.status === 'Completed') {
      this.isSimulationRunning = false;
      await this.completeSimulation(jobId, false);
    } else {
      this.isSimulationRunning = false;
      this.addLog('error', `Simulation ended with status: ${job.status}${job.message ? ' - ' + job.message : ''}`);
    }
  }

  private async resumeRedeemJob(job: SafeBoxBatchJobSummaryResponse): Promise<void> {
    const jobId = job.jobId!;
    this.stopRedeemMonitoring();

    this.redeemJobId = jobId;
    this.redeemEverStarted = true;
    this.redeemCompleted = false;
    this.redeemResults = [];
    this.redeemErrorReportS3Url = null;
    this.showRedeemConfirmBanner = false;
    this.redeemProgress = {
      total: job.totalRows ?? 0,
      processed: job.processedRows ?? 0,
      success: job.successCount ?? 0,
      failed: job.failedCount ?? 0,
      percentage: job.progressPercentage ?? 0
    };

    this.addLog('info', `Resuming redeem job ${jobId} (status: ${job.status})`);

    if (job.status === 'Running') {
      this.isRedeemRunning = true;
      this.startRedeemMonitoring(jobId);
    } else if (job.status === 'Completed') {
      this.isRedeemRunning = false;
      await this.completeRedeem(jobId, false);
    } else {
      this.isRedeemRunning = false;
      this.addLog('error', `Redeem job ended with status: ${job.status}${job.message ? ' - ' + job.message : ''}`);
    }
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  private scrollToBottom(): void {
    if (this.logContainer) {
      try {
        this.logContainer.nativeElement.scrollTop = this.logContainer.nativeElement.scrollHeight;
      } catch (err) {
        console.error('Error when try scroll to bottom! ', err);
      }
    }
  }

  // ---------- CSV upload & validation ----------

  onFileSelect(event: any): void {
    const files = event.addedFiles;
    if (files && files.length > 0) {
      const file = files[0];

      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.snackBar.open('Please select a CSV file', 'Close', { duration: 3000 });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.snackBar.open('File size must be less than 5MB', 'Close', { duration: 3000 });
        return;
      }

      this.validateCsvFormat(file);
    }
  }

  onFileRemove(file: File): void {
    const index = this.selectedFiles.indexOf(file);
    if (index > -1) {
      this.selectedFiles.splice(index, 1);
      this.previewRows = [];
      this.addLog('info', `File removed: ${file.name}`);
    }
  }

  private validateCsvFormat(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const csvContent = e.target.result as string;
        const validation = this.validateCsvContent(csvContent);

        if (validation.isValid) {
          this.selectedFiles = [file];
          this.previewRows = validation.rows ?? [];
          this.addLog('success', `File validated successfully: ${file.name} (${this.previewRows.length} rows found)`);
        } else {
          this.previewRows = [];
          this.snackBar.open(`CSV validation failed: ${validation.error}`, 'Close', { duration: 5000 });
          this.addLog('error', `CSV validation failed: ${validation.error}`);
        }
      } catch (error) {
        this.previewRows = [];
        this.snackBar.open('Error reading CSV file', 'Close', { duration: 3000 });
        this.addLog('error', `Error reading CSV file: ${error}`);
      }
    };

    reader.onerror = () => {
      this.snackBar.open('Error reading file', 'Close', { duration: 3000 });
      this.addLog('error', 'Error reading file');
    };

    reader.readAsText(file);
  }

  private validateCsvContent(csvContent: string): { isValid: boolean; error?: string; rows?: SimulateRedeemRowLike[] } {
    const lines = csvContent.trim().split('\n').filter(line => line.trim().length > 0);

    if (lines.length < 2) {
      return { isValid: false, error: 'CSV file must contain a header row and at least one data row' };
    }

    if (lines.length > 501) {
      return { isValid: false, error: `CSV file contains ${lines.length - 1} data rows but maximum allowed is 500 rows` };
    }

    const headerRow = lines[0].trim();
    const columns = headerRow.split(/[\t,]/).map(col => col.trim().toLowerCase());

    const requiredColumns = ['user_id', 'code'];
    const missingColumns = requiredColumns.filter(col => !columns.includes(col));
    if (missingColumns.length > 0) {
      return {
        isValid: false,
        error: `Missing required columns: ${missingColumns.join(', ')}. Required columns are: ${requiredColumns.join(', ')}`
      };
    }

    const userIdIndex = columns.indexOf('user_id');
    const codeIndex = columns.indexOf('code');

    const rows = lines.slice(1)
      .map(line => line.split(/[\t,]/).map(part => part.trim().replace(/^"|"$/g, '')))
      .map(parts => ({ userId: parts[userIdIndex], code: parts[codeIndex] }))
      .filter(row => row.userId && row.code);

    return { isValid: true, rows };
  }

  downloadTemplate(): void {
    const csvContent = [
      'user_id,code',
      'user123,SBX-ABC123',
      'user456,SBX-DEF456',
      'user789,SBX-GHI789'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'simulate_redeem_safebox_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);

    this.addLog('info', 'CSV template downloaded');
    this.addLog('info', 'Required fields: user_id, code');
  }

  // ---------- Simulation phase ----------

  canStartSimulation(): boolean {
    return this.selectedFiles.length > 0 && !this.isSimulationRunning;
  }

  async startSimulation(): Promise<void> {
    if (!this.canStartSimulation()) {
      return;
    }

    this.isSimulationRunning = true;
    this.simulationCompleted = false;
    this.simulationEverStarted = true;
    this.resetRedeemState();
    this.resetSimulationProgress();
    this.simulationResults = [];
    this.selectedRowKeys.clear();

    this.addLog('info', 'Starting SafeBox redeem simulation...');
    this.addLog('info', `Advertiser=${this.advertiserId}`);

    try {
      const file = this.selectedFiles[0];
      this.addLog('info', 'Uploading CSV file to S3...');
      const uploadKey = await this.uploadFileToS3(file);

      if (!uploadKey) {
        this.addLog('error', 'Failed to upload CSV file to S3');
        this.isSimulationRunning = false;
        return;
      }

      this.addLog('success', 'CSV file uploaded successfully to S3');
      this.addLog('info', 'Calling simulate-redeem API...');

      const startResponse = await lastValueFrom(
        this.safeBoxClient.simulateRedeemSafeBoxCodes(new SafeBoxStartSimulationRequest({ csvS3Url: uploadKey, isCheckLimitRule: this.checkLimitRule }))
      );
      const simulationId = startResponse.simulationId!;
      this.simulationId = simulationId;
      this.selectedHistoryJobId = simulationId;
      this.addLog('success', `Simulation started. Simulation ID: ${simulationId}`);

      this.startSimulationMonitoring(simulationId);
      this.addJobToHistoryImmediately(simulationId, 'Simulate');
    } catch (error) {
      this.addLog('error', `Simulation failed: ${error}`);
      this.snackBar.open('Simulation failed. Please check the logs for details.', 'Close', { duration: 5000 });
      this.isSimulationRunning = false;
    }
  }

  private async uploadFileToS3(file: File): Promise<string | null> {
    try {
      // No "/" in the key: the backend read path (SafeBoxBatchRedeemService.ReadRowsCsv) URL-escapes this key the
      // same way MigrationService does, which would percent-encode a literal "/" and break the S3 key lookup.
      const uploadKey = `simulate-redeem-safebox-${GUID.newGuid().replace(/-/g, '')}.${file.name.split('.').pop()}`;
      // Uses the same upload endpoint/bucket as migration campaigns (CampaignAdminController.GetUploadUrl),
      // not the SafeBox product-SKU import bucket used by getUploadUrlS3 - that one is a separate CSV format/pipeline.
      const uploadUrl = await lastValueFrom(this.campaignAdminClient.getUploadUrl(uploadKey));
      const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl as string, file));

      if (uploadSuccess) {
        this.addLog('success', `File uploaded to S3 with key: ${uploadKey}`);
        return uploadKey;
      }
      this.addLog('error', 'Failed to upload file to S3');
      return null;
    } catch (error) {
      this.addLog('error', `S3 upload error: ${error}`);
      return null;
    }
  }

  private startSimulationMonitoring(simulationId: string): void {
    this.addLog('info', 'Monitoring simulation progress every 3 seconds until complete...');

    this.simulationMonitorSub = interval(3000).pipe(
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      try {
        const progress = await lastValueFrom(this.safeBoxClient.getSimulateRedeemProgress(simulationId));

        this.simulationProgress = {
          total: progress.totalRows ?? 0,
          processed: progress.processedRows ?? 0,
          valid: progress.validCount ?? 0,
          invalid: progress.invalidCount ?? 0,
          percentage: progress.progressPercentage ?? 0
        };
        if (progress.errorReportS3Url) {
          this.simulationErrorReportS3Url = progress.errorReportS3Url;
        }
        this.addLog('info', `Simulation progress: ${this.simulationProgress.processed}/${this.simulationProgress.total} (${this.simulationProgress.percentage}%)`);

        if (progress.status !== 'Running') {
          this.stopSimulationMonitoring();

          if (progress.status === 'Completed') {
            await this.completeSimulation(simulationId);
          } else {
            this.isSimulationRunning = false;
            this.addLog('error', `Simulation ended with status: ${progress.status}`);
          }
          this.loadJobHistory();
        }
      } catch (error) {
        this.addLog('error', `Error monitoring simulation: ${error}`);
        this.stopSimulationMonitoring();
        this.isSimulationRunning = false;
      }
    });
  }

  private stopSimulationMonitoring(): void {
    if (this.simulationMonitorSub) {
      this.simulationMonitorSub.unsubscribe();
      this.simulationMonitorSub = null;
    }
  }

  private async completeSimulation(simulationId: string, showNotification = true): Promise<void> {
    try {
      this.simulationResults = await lastValueFrom(this.safeBoxClient.getSimulateRedeemResults(simulationId));
      this.selectAllRedeemable();
      this.isSimulationRunning = false;
      this.simulationCompleted = true;
      this.addLog('success', `Simulation completed: ${this.simulationProgress.valid} redeemable, ${this.simulationProgress.invalid} not redeemable`);
      if (showNotification) {
        this.snackBar.open('Simulation completed', 'Close', { duration: 4000 });
      }
    } catch (error) {
      this.addLog('error', `Failed to fetch simulation results: ${error}`);
      this.isSimulationRunning = false;
    }
  }

  isMonitoringSimulation(): boolean {
    return this.isSimulationRunning && this.simulationMonitorSub && !this.simulationMonitorSub.closed;
  }

  stopSimulationManually(): void {
    this.stopSimulationMonitoring();
    this.isSimulationRunning = false;
    this.addLog('warning', 'Simulation monitoring stopped manually');
  }

  private resetSimulationProgress(): void {
    this.simulationProgress = { total: 0, processed: 0, valid: 0, invalid: 0, percentage: 0 };
  }

  getSimulationProgressBarColor(): 'primary' | 'accent' | 'warn' {
    if (this.simulationProgress.invalid > 0) {
      return 'warn';
    } else if (this.simulationProgress.percentage === 100) {
      return 'accent';
    }
    return 'primary';
  }

  // ---------- Results table / selection ----------

  rowKey(row: SimulateRedeemRowLike): string {
    return `${row.userId}::${row.code}`;
  }

  statusMeta(status: string | undefined): StatusMeta {
    return (status && STATUS_META[status]) ?? { label: status ?? 'Unknown', icon: 'help', cssClass: 'status-warning' };
  }

  isRowSelected(row: SafeBoxSimulationRowResponse): boolean {
    return this.selectedRowKeys.has(this.rowKey(row));
  }

  toggleRow(row: SafeBoxSimulationRowResponse): void {
    if (!row.isRedeemable) {
      return;
    }
    const key = this.rowKey(row);
    if (this.selectedRowKeys.has(key)) {
      this.selectedRowKeys.delete(key);
    } else {
      this.selectedRowKeys.add(key);
    }
  }

  get redeemableRows(): SafeBoxSimulationRowResponse[] {
    return this.simulationResults.filter(r => r.isRedeemable);
  }

  get selectedRows(): SafeBoxSimulationRowResponse[] {
    return this.simulationResults.filter(r => this.selectedRowKeys.has(this.rowKey(r)));
  }

  get selectedCount(): number {
    return this.selectedRowKeys.size;
  }

  isAllRedeemableSelected(): boolean {
    const redeemable = this.redeemableRows;
    return redeemable.length > 0 && redeemable.every(r => this.selectedRowKeys.has(this.rowKey(r)));
  }

  masterToggle(): void {
    if (this.isAllRedeemableSelected()) {
      this.deselectAll();
    } else {
      this.selectAllRedeemable();
    }
  }

  selectAllRedeemable(): void {
    this.selectedRowKeys = new Set(this.redeemableRows.map(r => this.rowKey(r)));
  }

  deselectAll(): void {
    this.selectedRowKeys.clear();
  }

  downloadSimulationReport(): void {
    const blob = new Blob([JSON.stringify(this.simulationResults, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `simulate_redeem_report_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    this.addLog('info', 'Simulation report downloaded');
  }

  downloadSimulationErrors(): void {
    if (this.simulationErrorReportS3Url) {
      const link = document.createElement('a');
      link.href = this.simulationErrorReportS3Url;
      link.target = '_blank';
      link.download = `simulate_redeem_errors_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      this.addLog('info', 'Error report downloaded from server');
      return;
    }

    const notRedeemable = this.simulationResults.filter(r => !r.isRedeemable);
    const csvContent = [
      'user_id,code,status,message',
      ...notRedeemable.map(r => `"${r.userId}","${r.code}","${r.status}","${(r.message ?? '').replace(/"/g, '""')}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `simulate_redeem_errors_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    this.addLog('info', 'Error report downloaded from results');
  }

  // ---------- Confirm & redeem phase ----------

  requestConfirmRedeem(): void {
    if (this.selectedCount === 0) {
      this.snackBar.open('Select at least one redeemable row first', 'Close', { duration: 3000 });
      return;
    }
    this.showRedeemConfirmBanner = true;
  }

  cancelRedeemConfirm(): void {
    this.showRedeemConfirmBanner = false;
  }

  async proceedWithRedeem(): Promise<void> {
    this.showRedeemConfirmBanner = false;
    await this.executeRedeem();
  }

  private resetRedeemState(): void {
    this.isRedeemRunning = false;
    this.redeemCompleted = false;
    this.redeemEverStarted = false;
    this.redeemJobId = null;
    this.redeemResults = [];
    this.redeemErrorReportS3Url = null;
    this.showRedeemConfirmBanner = false;
    this.resetRedeemProgress();
    this.stopRedeemMonitoring();
  }

  private resetRedeemProgress(): void {
    this.redeemProgress = { total: 0, processed: 0, success: 0, failed: 0, percentage: 0 };
  }

  private async executeRedeem(): Promise<void> {
    if (!this.simulationId || this.selectedCount === 0) {
      return;
    }

    const rowsToRedeem = this.selectedRows.map(r => new SafeBoxConfirmRedeemRow({ userId: r.userId, code: r.code }));

    this.isRedeemRunning = true;
    this.redeemCompleted = false;
    this.redeemEverStarted = true;
    this.redeemResults = [];
    this.resetRedeemProgress();

    this.addLog('info', `Confirming redeem for ${rowsToRedeem.length} selected row(s)...`);

    try {
      const confirmResponse = await lastValueFrom(
        this.safeBoxClient.confirmRedeemSafeBoxCodes(this.simulationId, new SafeBoxConfirmRedeemRequest({ rows: rowsToRedeem }))
      );
      const redeemJobId = confirmResponse.redeemJobId!;
      this.redeemJobId = redeemJobId;
      this.selectedHistoryJobId = redeemJobId;
      this.addLog('success', `Redeem job started. Job ID: ${redeemJobId}`);
      this.startRedeemMonitoring(redeemJobId);
      this.loadJobHistory();
    } catch (error) {
      this.addLog('error', `Failed to start redeem job: ${error}`);
      this.snackBar.open('Failed to start redeem. Please check the logs for details.', 'Close', { duration: 5000 });
      this.isRedeemRunning = false;
    }
  }

  private startRedeemMonitoring(redeemJobId: string): void {
    this.addLog('info', 'Monitoring redeem progress every 3 seconds until complete...');

    this.redeemMonitorSub = interval(3000).pipe(
      takeUntil(this.destroy$)
    ).subscribe(async () => {
      try {
        const progress = await lastValueFrom(this.safeBoxClient.getRedeemProgress(redeemJobId));

        this.redeemProgress = {
          total: progress.totalRows ?? 0,
          processed: progress.processedRows ?? 0,
          success: progress.successCount ?? 0,
          failed: progress.failedCount ?? 0,
          percentage: progress.progressPercentage ?? 0
        };
        if (progress.errorReportS3Url) {
          this.redeemErrorReportS3Url = progress.errorReportS3Url;
        }
        this.addLog('info', `Redeem progress: ${this.redeemProgress.processed}/${this.redeemProgress.total} (${this.redeemProgress.percentage}%)`);

        if (progress.status !== 'Running') {
          this.stopRedeemMonitoring();

          if (progress.status === 'Completed') {
            await this.completeRedeem(redeemJobId);
          } else {
            this.isRedeemRunning = false;
            this.addLog('error', `Redeem job ended with status: ${progress.status}`);
          }
          this.loadJobHistory();
        }
      } catch (error) {
        this.addLog('error', `Error monitoring redeem job: ${error}`);
        this.stopRedeemMonitoring();
        this.isRedeemRunning = false;
      }
    });
  }

  private stopRedeemMonitoring(): void {
    if (this.redeemMonitorSub) {
      this.redeemMonitorSub.unsubscribe();
      this.redeemMonitorSub = null;
    }
  }

  private async completeRedeem(redeemJobId: string, showNotification = true): Promise<void> {
    try {
      this.redeemResults = await lastValueFrom(this.safeBoxClient.getRedeemResults(redeemJobId));
      this.isRedeemRunning = false;
      this.redeemCompleted = true;
      this.addLog('success', `Redeem completed: ${this.redeemProgress.success} successful, ${this.redeemProgress.failed} failed`);
      if (showNotification) {
        this.snackBar.open('Redeem completed', 'Close', { duration: 4000 });
      }
    } catch (error) {
      this.addLog('error', `Failed to fetch redeem results: ${error}`);
      this.isRedeemRunning = false;
    }
  }

  isMonitoringRedeem(): boolean {
    return this.isRedeemRunning && this.redeemMonitorSub && !this.redeemMonitorSub.closed;
  }

  stopRedeemManually(): void {
    this.stopRedeemMonitoring();
    this.isRedeemRunning = false;
    this.addLog('warning', 'Redeem monitoring stopped manually');
  }

  getRedeemProgressBarColor(): 'primary' | 'accent' | 'warn' {
    if (this.redeemProgress.failed > 0) {
      return 'warn';
    } else if (this.redeemProgress.percentage === 100) {
      return 'accent';
    }
    return 'primary';
  }

  downloadRedeemReport(): void {
    const blob = new Blob([JSON.stringify(this.redeemResults, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `redeem_report_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    window.URL.revokeObjectURL(url);
    this.addLog('info', 'Redeem report downloaded');
  }

  downloadRedeemErrors(): void {
    if (this.redeemErrorReportS3Url) {
      const link = document.createElement('a');
      link.href = this.redeemErrorReportS3Url;
      link.target = '_blank';
      link.download = `redeem_errors_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      this.addLog('info', 'Error report downloaded from server');
      return;
    }

    const failed = this.redeemResults.filter(r => !r.success);
    const csvContent = [
      'user_id,code,message',
      ...failed.map(r => `"${r.userId}","${r.code}","${(r.message ?? '').replace(/"/g, '""')}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `redeem_errors_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    this.addLog('info', 'Error report downloaded from results');
  }

  // ---------- Reset / logging ----------

  resetAll(): void {
    this.selectedFiles = [];
    this.previewRows = [];
    this.isSimulationRunning = false;
    this.simulationCompleted = false;
    this.simulationEverStarted = false;
    this.simulationId = null;
    this.simulationResults = [];
    this.simulationErrorReportS3Url = null;
    this.resetSimulationProgress();
    this.stopSimulationMonitoring();
    this.selectedRowKeys.clear();
    this.resetRedeemState();
    this.logs = [];
    this.addLog('info', 'Form reset');
  }

  clearLogs(): void {
    this.logs = [];
  }

  addLog(level: 'info' | 'success' | 'warning' | 'error', message: string): void {
    this.logs.push({ timestamp: new Date(), level, message });
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }
    this.shouldScrollToBottom = true;
  }

  trackByLogEntry(index: number, log: SimulateRedeemLog): string {
    return `${log.timestamp.getTime()}-${log.level}-${index}`;
  }

  trackByRowKey(_index: number, row: SafeBoxSimulationRowResponse | SafeBoxRedeemRowResponse): string {
    return this.rowKey(row);
  }
}

interface SimulateRedeemRowLike {
  userId?: string;
  code?: string;
}
